import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Typography,
  Upload,
  message,
  Image,
  Input,
  InputNumber,
  Radio,
  Switch,
} from "antd";
import { Form } from "@ant-design/compatible";
import { UploadOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  EDIT_LOYALTY,
  EDIT_LOYALTY_SUCCESS,
  LIST_LOYALTIES,
} from "../../constants";

import "../../index.scss";
import strings from "../../../../../../../../strings";

const EditLoyalty = ({ visible, closeModal, currLoyalty, form }) => {
  const [state, setstate] = useState({
    imageUrl: "",
    loading: false,
  });
  const [showExpiration, setShowExpiration] = useState(!!currLoyalty.expiration);
  const [ imageUrL, setImageUrl ] = useState(null);
  const [amountValue, setAmountValue] = useState(currLoyalty?.currency_value || null);
  const [pointsValue, setPointsValue] = useState(currLoyalty?.points || null);
  const [showDecimalWarning, setShowDecimalWarning] = useState(false);

  const { id: loyaltyId, name, currency_value } = currLoyalty;

  useEffect(()=>{
    if(currLoyalty.image_url){
      setImageUrl(currLoyalty.image_url);
    }
  }, []);

  useEffect(() => {
    if (currLoyalty) {
      setstate({ imageUrl: currLoyalty.image_url, loading: false });
    }
  }, [currLoyalty, currency_value, form]);

  const [editLoyalty, { loading: isEditLoyaltyLoading }] = useMutation(
    EDIT_LOYALTY,
    {
      onCompleted: () => {
        form.resetFields();

        closeModal();

        Modal.success({
          title: EDIT_LOYALTY_SUCCESS,
          okButtonProps: {
            id: "btn-component",
          },
        });
      },
      onError: error => {
        message.error(error.message, 5);
      },
      refetchQueries: [{ query: LIST_LOYALTIES }],
    }
  );

  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  // Check if the points-to-currency ratio will result in decimal points for customers
  const checkForDecimals = (amount, points) => {
    if (!amount || !points || amount <= 0 || points <= 0) {
      setShowDecimalWarning(false);
      return;
    }

    // Check if the ratio creates whole number points per currency unit
    const pointsPerCurrency = points / amount;
    const hasDecimal = !Number.isInteger(pointsPerCurrency) && pointsPerCurrency < 1;
    
    // Also check if currency per point is a clean division
    const currencyPerPoint = amount / points;
    const currencyHasDecimal = !Number.isInteger(currencyPerPoint) && currencyPerPoint < 1;
    
    setShowDecimalWarning(hasDecimal || currencyHasDecimal);
  };

  const handleAmountChange = (value) => {
    setAmountValue(value);
    checkForDecimals(value, pointsValue);
  };

  const handlePointsChange = (value) => {
    setPointsValue(value);
    checkForDecimals(amountValue, value);
  };

  useEffect(() => {
    // Check on initial load
    checkForDecimals(currLoyalty?.currency_value, currLoyalty?.points);
  }, [currLoyalty]);

  const uploadProps = {
    name: "image",
    action: `${process.env.REACT_APP_MULTI_LEVEL_BASE_URL}/upload`,
    onChange(info) {
      if (info.file.status === "done") {
        message.success(`${info.file.name} ${strings.fileUploadSuccessful}`);
        setstate({
          ...state,
          imageUrl: info.file.response.imageCloudData.secure_url,
        });
        setImageUrl(info.file.response.imageCloudData.secure_url);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} ${strings.fileUploadFailed}.`);
      }
    },
  };

  const onEdit = async values => {
    const data = {
      name: values.name,
      currency_value: Number(values.amount),
      points: Number(values.points),
      verification: values.verification,
      expiration: Number(values.expiration),
      // magnification: Number(values.magnification)
    };

    if (imageUrL) {
      data.image_url = imageUrL || currLoyalty.image_url;
    } else return message.error("No image provided");

    // console.log(data, imageUrL);
    await editLoyalty({
      variables: {
        id: loyaltyId,
        data,
      },
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onEdit(values);
    } catch (error) {
      console.log("Validate Failed:", error);
    }
  };

  const { getFieldDecorator } = form;

  return (
    <Modal
      title={`Edit ${name}`}
      open={visible}
      onCancel={closeModal}
      footer={[
        <Button key="cancel" onClick={closeModal}>
          {strings.cancel}
        </Button>,
        <Button
          key="edit"
          type="primary"
          loading={isEditLoyaltyLoading}
          onClick={() => handleSubmit()}
        >
          {strings.edit}
        </Button>,
      ]}
    >
      <Form layout="vertical" name="create_loyalty" id="loyaltyForm">
        <Form.Item name="name" label={strings.name} className="wrap">
          {getFieldDecorator("name", {
            initialValue: currLoyalty?.name,
            rules: [{ required: true }],
          })(<Input />)}
        </Form.Item>

         {/* <Form.Item name="magnification" label={strings.magnification} className="wrap">
            {getFieldDecorator("magnification", {
              initialValue: currLoyalty?.magnification,
              rules: [{ required: true }],
            })(<InputNumber style={{width: "100%"}} />)}
          </Form.Item> */}

        {imageUrL && <Image src={imageUrL} style={{height:100, width: 100}} preview alt="loyalty" />}
        <Form.Item
          name="upload"
          label={strings.loyaltyArtwork}
          className="wrap"
        >
          
          {getFieldDecorator("upload", {
            valuePropName: "fileList",
            getValueFromEvent: normFile,
          })(
            <Upload listType="picture" multiple={false} {...uploadProps}>
              <Button icon={<UploadOutlined />}>{strings.clickToUpload}</Button>
            </Upload>
          )}
        </Form.Item>

        <Typography.Text>
          <p>{strings.amountToPointsRatio}</p>
        </Typography.Text>

        <div className="curr_points_ratio">
          <Form.Item
            name="amount"
            label={strings.amount + " (N)"}
            className="wrap"
          >
            {getFieldDecorator("amount", {
              initialValue: currLoyalty?.currency_value,
              rules: [{ required: true }],
            })(<InputNumber style={{width: "100%"}} onChange={handleAmountChange} />)}
          </Form.Item>

          <span className="equal_sign">===</span>

          <Form.Item name="points" label={strings.points} className="wrap">
            {getFieldDecorator("points", {
              initialValue: currLoyalty?.points,
              rules: [{ required: true }],
            })(<InputNumber style={{width: "100%"}} onChange={handlePointsChange} />)}
          </Form.Item>
        </div>
        
        {showDecimalWarning && (
          <div style={{ 
            marginBottom: 16, 
            padding: '12px 16px', 
            background: '#fff7e6', 
            border: '1px solid #ffd666',
            borderRadius: '4px'
          }}>
            <Typography.Text style={{ color: '#d48806', fontWeight: 500 }}>
              ⚠️ Warning: This ratio may result in decimal points for customer balances
            </Typography.Text>
            <br />
            <Typography.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
              With {amountValue} NGN = {pointsValue} points, customers will earn {(pointsValue / amountValue).toFixed(4)} points per NGN spent, which may create fractional points.
              <br />
              <strong>Best Practice:</strong> Use whole-number ratios like 1:1, 10:1, 100:10, or 1:10 for easier customer understanding and better engagement.
            </Typography.Text>
          </div>
        )}
        
        <Typography.Text  style={{marginBottom:20, display: "inline-block"}}>
          {strings.recommendedAmountToPointRatio}
        </Typography.Text>

        <Form.Item
          name="verification"
          label="Verify With Token"
          className="wrap mt-2"
        >
          <Typography.Text>
            Verify with token before redeeming points{" "}
          </Typography.Text>
          {getFieldDecorator("verification", {
            initialValue: currLoyalty.verification,
          })(<Switch defaultChecked={currLoyalty.verification} />)}
        </Form.Item>

        <div>
          <Radio.Group
            onChange={val =>
              val.target.value === 1
                ? setShowExpiration(false)
                : setShowExpiration(true)
            }
            defaultValue={currLoyalty.expiration ? 2 : 1}
          >
            <Radio value={1}>Points do not expire</Radio>
            <Radio value={2}>Points expire</Radio>
          </Radio.Group>
        </div>

        {showExpiration && (
          <div className="mt-2">
            <Form.Item
              name="expiration"
              label="Set Points Expiration"
              className="wrap mt-2"
            >
              {getFieldDecorator("expiration", {
                initialValue: currLoyalty.expiration,
              })(<InputNumber min={1} style={{ width: "60px" }} />)}
              <Typography.Text> Days after point is credited</Typography.Text>
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default Form.create()(EditLoyalty);
